import styled from 'styled-components/macro';
import { breakpoints, screenMd } from 'constants/styles/breakpoints';

export const ModalShell = styled.div`
  background: var(--white, #fff);
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  padding: 30px;

  @media (max-width: ${breakpoints.sm - 1}px) {
    box-sizing: border-box;
    height: 100vh;
    max-height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
  }

  ${screenMd} {
    border-radius: 20px;
  }
`;

export const Header = styled.header`
  align-items: center;
  border-bottom: 1px solid var(--dusty-gray, #e9e9ed);
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
`;

export const Content = styled.main`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
`;

export const AccountNameField = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;

  @media (max-width: ${breakpoints.sm - 1}px) {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }
`;

export const AccountNameLabel = styled.label`
  color: var(--black-dark, #1a2535);
  flex: 0 0 auto;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

export const AccountNameInput = styled.input`
  background: var(--ghost-white, #f2f2f7);
  border: 1px solid transparent;
  border-radius: 10px;
  box-sizing: border-box;
  color: var(--black-dark, #1a2535);
  flex: 1;
  font-family: Lato, sans-serif;
  font-size: 14px;
  height: 44px;
  min-height: 44px;
  min-width: 0;
  padding: 0 12px;

  @media (max-width: ${breakpoints.sm - 1}px) {
    flex: 0 0 44px;
    width: 100%;
  }

  &:focus {
    border-color: var(--lilac, #a793f6);
    outline: none;
  }
`;

export const PlanOptions = styled.fieldset`
  border: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  min-width: 0;
  padding: 0;
`;

export const PlanLegend = styled.legend`
  color: var(--black-dark, #1a2535);
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 10px;
  padding: 0;
`;

export const PlanOption = styled.label<{ focused: boolean; disabled?: boolean }>`
  align-items: center;
  background: ${({ focused }) =>
    focused ? 'rgba(211, 201, 250, 0.15)' : 'var(--grey-lighter-50, rgba(242, 242, 247, 0.5))'};
  border: 2px solid ${({ focused }) => (focused ? 'var(--lilac, #a793f6)' : 'transparent')};
  border-radius: 7px;
  box-sizing: border-box;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  flex: 0 0 auto;
  gap: 15px;
  min-height: 46px;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  padding: 10px;
  width: 100%;

  @media (max-width: ${breakpoints.sm - 1}px) {
    align-items: center;
    column-gap: 15px;
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr);
    height: auto;
    row-gap: 10px;
  }

  ${({ disabled }) =>
    !disabled &&
    `
      &:hover {
        border-color: var(--lilac, #a793f6);
      }
    `}
`;

export const PlanRadio = styled.input<{ focused: boolean }>`
  appearance: none;
  background: var(--white, #fff);
  border: 2px solid var(--dusty-gray, #e9e9ed);
  border-radius: 20px;
  flex: 0 0 20px;
  height: 20px;
  margin: 0;
  mix-blend-mode: multiply;
  position: relative;
  width: 20px;

  &:checked {
    border-color: ${({ focused }) =>
      focused ? 'var(--lilac, #a793f6)' : 'var(--dusty-gray, #e9e9ed)'};
  }

  &:checked::after {
    background: var(--lilac, #a793f6);
    border-radius: 50%;
    content: '';
    height: 10px;
    left: 3px;
    position: absolute;
    top: 3px;
    width: 10px;
  }

  &:focus-visible {
    outline: 2px solid var(--lilac, #a793f6);
    outline-offset: 2px;
  }
`;

export const Addons = styled.section<{ $overlayOpen: boolean; $visible: boolean }>`
  --addons-max-height: 260px;

  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: ${({ $visible }) => ($visible ? 'var(--addons-max-height)' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  position: relative;
  transform: translateY(${({ $visible }) => ($visible ? 0 : '-8px')});
  transition: max-height 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out,
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: max-height, opacity, transform;
  z-index: ${({ $overlayOpen, $visible }) => ($overlayOpen ? 1000 : $visible ? 1 : 'auto')};

  @media (max-width: ${breakpoints.sm - 1}px) {
    --addons-max-height: 600px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const AddonsTitle = styled.h3`
  color: var(--black-dark, #1a2535);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  margin: 0;
`;

export const AddonList = styled.div`
  background: var(--grey-lighter-50, rgba(242, 242, 247, 0.5));
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 10px;
  transition: gap 280ms cubic-bezier(0.22, 1, 0.36, 1), padding 280ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: gap, padding;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const AddonEntry = styled.div<{ $transitionState: 'entering' | 'leaving' | 'visible' }>`
  --addon-entry-max-height: 60px;

  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: ${({ $transitionState }) =>
    $transitionState === 'leaving' ? '0' : 'var(--addon-entry-max-height)'};
  opacity: ${({ $transitionState }) => ($transitionState === 'leaving' ? 0 : 1)};
  overflow: ${({ $transitionState }) => ($transitionState === 'visible' ? 'visible' : 'hidden')};
  pointer-events: ${({ $transitionState }) => ($transitionState === 'leaving' ? 'none' : 'auto')};
  position: relative;
  transform: translateY(${({ $transitionState }) => ($transitionState === 'leaving' ? '-6px' : 0)});
  transition: max-height 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out,
    transform 280ms cubic-bezier(0.22, 1, 0.36, 1);

  ${({ $transitionState }) =>
    $transitionState === 'entering' &&
    `
      animation: account-management-addon-enter 280ms cubic-bezier(0.22, 1, 0.36, 1);
    `}

  @keyframes account-management-addon-enter {
    from {
      max-height: 0;
      opacity: 0;
      transform: translateY(-6px);
    }

    to {
      max-height: var(--addon-entry-max-height);
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${breakpoints.sm - 1}px) {
    --addon-entry-max-height: 180px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

export const AddonCheckbox = styled.div`
  .ant-checkbox-wrapper > span:not(.ant-checkbox) {
    display: none;
  }
`;

export const AddonOption = styled.div<{ $disabled: boolean; separator: boolean }>`
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: grid;
  gap: 15px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  height: 32px;
  position: relative;

  @media (max-width: ${breakpoints.sm - 1}px) {
    align-items: start;
    grid-template-columns: auto minmax(0, 1fr);
    height: auto;
    min-height: 32px;
    row-gap: 8px;
  }

  ${({ separator }) =>
    separator &&
    `
      &::before {
        background: var(--grey-lighter, #f2f2f7);
        content: '';
        height: 1px;
        left: 0;
        position: absolute;
        right: 0;
        top: -9px;
      }
    `}
`;

export const AddonControls = styled.div`
  align-items: center;
  display: flex;
  gap: 15px;
  white-space: nowrap;

  @media (max-width: ${breakpoints.sm - 1}px) {
    flex-wrap: wrap;
    grid-column: 2 / -1;
    justify-content: flex-start;
    margin-left: 0;
  }
`;

export const DateSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const DateHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  @media (max-width: ${breakpoints.sm - 1}px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
`;

export const DateFlags = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: flex-end;

  @media (max-width: ${breakpoints.sm - 1}px) {
    justify-content: flex-start;
    width: 100%;
  }
`;

export const DateTitle = styled.h3`
  color: var(--black-dark, #1a2535);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  margin: 0;
`;

export const FreeTrial = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

export const FreeTrialLabel = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const DateFields = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: ${breakpoints.sm - 1}px) {
    gap: 10px;
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const DateCard = styled.div`
  align-items: center;
  background: var(--grey-lighter, #f2f2f7);
  border-radius: 10px;
  display: flex;
  gap: 8px;
  height: 44px;
  min-width: 0;
  padding: 0 12px;
`;

export const DatePickerControl = styled.div`
  flex: 1;
  min-width: 0;

  .ant-picker {
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    height: auto;
    padding: 0;
    width: 100%;
  }

  .ant-picker-focused {
    box-shadow: none;
  }

  .ant-picker-input > input {
    color: var(--black-darkest, #111a27);
    font-family: Lato, sans-serif;
    font-feature-settings: 'liga' off, 'clig' off;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
  }

  .ant-picker-input > input::placeholder {
    color: var(--light-gray-darker, #57575c) !important;
    font-weight: 400;
  }
`;

export const DateCardLabel = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
`;

export const DateCardValue = styled.time`
  color: var(--black-darkest, #111a27);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

export const StaffFields = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: ${breakpoints.sm - 1}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const StaffField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const StaffFieldLabel = styled.label`
  color: var(--black-dark, #1a2535);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

export const AddonName = styled.span`
  align-items: center;
  color: var(--black-dark, #1a2535);
  display: inline-flex;
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  flex-wrap: wrap;
  gap: 5px;
  line-height: 20px;
  min-width: 0;
`;

export const AddonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const CertificationSelectionStatus = styled.span`
  color: var(--black-darkest, #111a27);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  opacity: 0.6;
`;

export const AdvancedOptionsButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: 0;
  color: var(--lilac-dark, #7965c6);
  cursor: pointer;
  display: inline-flex;
  font-family: Lato, sans-serif;
  font-size: 12px;
  line-height: 14px;
  padding: 0;

  svg {
    margin-left: 4px;
  }
`;

export const CertificationOptions = styled.div<{ $visible: boolean }>`
  background: var(--white, #fff);
  border-radius: 10px;
  bottom: -269px;
  box-sizing: border-box;
  box-shadow: 0 30px 48px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 293px;
  left: 42px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow-x: hidden;
  overflow-y: ${({ $visible }) => ($visible ? 'auto' : 'hidden')};
  padding: 20px;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  position: absolute;
  top: auto;
  transform: translateY(${({ $visible }) => ($visible ? 0 : '-4px')});
  transition: opacity 180ms ease-out, transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
  width: 351px;
  z-index: 2;

  @media (max-width: ${breakpoints.sm - 1}px) {
    left: 0;
    max-width: 351px;
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CertificationOptionsTitle = styled.p`
  align-self: stretch;
  color: var(--black-dark, #1a2535);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  margin: 0;
`;

export const CertificationOption = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  min-height: 28px;
`;

export const CertificationDescription = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
`;

export const CertificationCode = styled.strong`
  color: var(--black-dark, #1a2535);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
`;

export const AddonLabel = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
`;

export const AddonCounter = styled.div`
  align-items: center;
  background: var(--grey-lighter, #f2f2f7);
  border-radius: 10px;
  box-sizing: border-box;
  display: flex;
  gap: 20px;
  min-height: 32px;
  padding: 3px 10px;
`;

export const AddonCounterValue = styled.input.attrs({ type: 'text' })`
  background: transparent;
  border: 0;
  color: var(--black-darkest, #111a27);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
  min-width: 14px;
  padding: 0;
  width: 32px;

  &:focus-visible {
    outline: 2px solid var(--lilac, #a793f6);
    outline-offset: 2px;
  }
`;

export const AddonCounterButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--lilac-dark, #7965c6);
  cursor: pointer;
  display: inline-flex;
  height: 10px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 10px;

  svg {
    height: 8px;
    width: 8px;
  }

  &:disabled {
    color: var(--dusty-gray, #e9e9ed);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--lilac, #a793f6);
    outline-offset: 1px;
  }
`;

export const PlanIcon = styled.img`
  flex: 0 0 32px;
  height: 32px;
  width: 32px;
`;

export const PlanLicenseControls = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  margin-left: auto;
  min-width: 0;

  @media (max-width: ${breakpoints.sm - 1}px) {
    flex-wrap: wrap;
    grid-column: 2 / -1;
    grid-row: 2;
    justify-content: flex-start;
    margin-left: 0;
    width: 100%;
  }
`;

export const PlanUnlimited = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`;

export const UnlimitedLabel = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
`;

export const PlanName = styled.span`
  align-items: center;
  color: var(--black-dark, #1a2535);
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  gap: 2px 8px;
  line-height: 20px;
  min-width: 0;
`;

export const PlanTitle = styled.span`
  flex-shrink: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LegacyBadge = styled.span`
  background: rgba(235, 159, 163, 0.5);
  border-radius: 4px;
  color: var(--color-accent-red-dark, #c11e05);
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  padding: 2px 4px;
`;

export const Actions = styled.footer<{ $isCreateMode: boolean }>`
  border-top: 1px solid var(--dusty-gray, #e9e9ed);
  display: flex;
  gap: 20px;
  justify-content: ${({ $isCreateMode }) => ($isCreateMode ? 'flex-end' : 'space-between')};
  padding-top: 20px;

  @media (max-width: ${breakpoints.sm - 1}px) {
    align-items: stretch;
    flex-direction: column;

    > button {
      width: 100%;
    }
  }
`;

export const PlanChangeDisclaimer = styled.p`
  align-self: center;
  color: var(--light-gray-darker, #57575c);
  font-family: Lato, sans-serif;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  margin: 0;
`;

export const Title = styled.h2`
  color: var(--black-darkest, #111a27);
  font-family: var(--font-family-base);
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  margin: 0;
  text-align: center;
`;

export const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: var(--black-darkest, #111a27);
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: -8px;
  padding: 8px;

  &:focus-visible {
    outline: 2px solid var(--lilac, #a793f6);
    outline-offset: 2px;
  }
`;
